import _ from 'lodash';

import ProductSlider from './components/ProductSlider/index.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';

//---helpers
import { globalLoader, MEDIA } from '@/helpers/variables';
import { checkIfHasVideoUrl, PRODUCT_SLIDER_ITEM_TYPE, PRODUCTS_ORDER_KEY } from '@/helpers';

//---mixins
import { dynamicHead } from '@/mixins/dynamic-head.js';

//---components
import BreadcrumbsSimple from '@/components/@general/BreadcrumbsSimple/index.vue';
import IconHeart from '@/components/icons/IconHeart.vue';
import ProductTabs from '@/modules/Product/components/ProductTabs/index.vue';
import ProductPopularCard from '@/modules/Product/components/ProductPopularCard/index.vue';
import ProductSeries from '@/modules/Product/components/ProductSeries/index.vue';
import ProductInfo from '@/modules/Product/components/ProductInfo/index.vue';

import defaultImage from '@/assets/img/svg/default-image.svg';
import IconPromoChristmas from '@/components/icons/IconPromoChristmas.vue';
import moment from 'moment/moment';
import IconPromoValentine from '@/components/icons/IconPromoValentine.vue';

export default {
  name: 'Product',
  mixins: [dynamicHead],
  components: {
    IconPromoValentine,
    IconPromoChristmas,
    ProductInfo,
    ProductSeries,
    ProductPopularCard,
    ProductTabs,
    IconHeart,
    BreadcrumbsSimple,
    ProductSlider
  },
  data() {
    return {
      MEDIA,
      clientPrices: [],
      seriesProducts: [],
      popularProducts: [],
      selectedProductPrice: null,
      productSlides: [],
      isFavorite: false
    };
  },

  async created() {
    globalLoader(true);
    let { slug } = this.$route.params;
    await this.getProduct({ slug, isResetProduct: true });
    this._setMetaParams();
    await this._loadContent();

    let trackEvent = {
      content_type: 'product',
      currency: 'UAH',
      content_ids: this.productData.unauthorizedProductPriceId
    };

    if (this.productData.brand) {
      trackEvent.content_category = this.productData.brand.title;
    }
    if (this.productData.brandSeries) {
      trackEvent.content_category = this.productData.brandSeries.title;
    }
    trackEvent.content_name = this.productData.title;
    trackEvent.value = this.productData.productPrices.data[0].price;

    fbq('track', 'ViewContent', { ...trackEvent });
  },
  computed: {
    ...mapGetters({
      productData: 'products/product',
      favourites: 'favorites/whichList',
      productsWithDefaultPrices: 'basket/productsWithDefaultPrices',
      isAuthenticated: 'auth/isAuthenticated',
      appSize: 'app/size'
    }),
    breadcrumbsList() {
      return this.productData && this.productData.brand
        ? [
            {
              title: this.productData.brand.title,
              route: { name: 'brand', params: { seria: this.productData.brand.slug } }
            }
          ]
        : [];
    },

    outlets() {
      return this.selectedProductPrice?.outletReasons?.data || [];
    },
    promotions() {
      let activities = this.selectedProductPrice?.promotion?.activities?.data || [];

      let dateNow = moment();

      return activities.filter((activity) => {
        let dateFrom = moment.utc(activity.dateFrom);
        let dateTo = moment.utc(activity.dateTo);

        return dateNow.isBetween(dateFrom, dateTo, null, '[)');
      });
    },
    isValentinesDayRange() {
      let now = new Date();
      let startDate = new Date(2026, 1, 9, 9, 0, 0);
      let endDate = new Date(2026, 1, 14, 23, 59, 59);
      return now >= startDate && now <= endDate;
    }
  },
  methods: {
    ...mapActions({
      addToFavorite: 'favorites/ADD_PRODUCT_TO_FAVOURITES',
      getProduct: 'products/GET_PRODUCT',
      removeFromFavorite: 'favorites/REMOVE_PRODUCT_IN_FAVORITE',
      getFavorites: 'favorites/GET_FAVORITES_LIST',

      getCatalogProducts: 'catalog/GET_CATALOG_PRODUCTS'
    }),
    ...mapMutations({
      changeLoginPopup: 'popups/CHANGE_LOGIN_POPUP'
    }),

    onUpdateProductPrice(selectedProductPrice) {
      let slides = [];
      let { images, image, image2, video } = this.productData;
      if (selectedProductPrice && selectedProductPrice.image) {
        slides.push({ link: selectedProductPrice.image, type: PRODUCT_SLIDER_ITEM_TYPE.IMAGE });
      }
      if (image && image.length) {
        slides.push({ link: image, type: PRODUCT_SLIDER_ITEM_TYPE.IMAGE });
      }
      if (image2 && image2.length) {
        slides.push({ link: image2, type: PRODUCT_SLIDER_ITEM_TYPE.IMAGE });
      }
      if (images && images.data.length) {
        let decoratedImages = images.data
          // remove with empty links
          .filter(({ link }) => link)
          // add type to every image
          .map((item) => ({
            ...item,
            type: checkIfHasVideoUrl(item.link) ? PRODUCT_SLIDER_ITEM_TYPE.VIDEO : PRODUCT_SLIDER_ITEM_TYPE.IMAGE
          }));

        slides = [...slides, ...decoratedImages];
      }
      if (video && video.length) {
        slides.push({ link: video, type: PRODUCT_SLIDER_ITEM_TYPE.VIDEO });
      }
      slides = _.uniqBy(slides, 'link');
      if (!slides.length) {
        slides = [{ link: defaultImage, type: PRODUCT_SLIDER_ITEM_TYPE.IMAGE }];
      }
      this.productSlides = slides;
      this.selectedProductPrice = selectedProductPrice;
    },

    async _loadContent() {
      let promiseCollection = [];

      promiseCollection.push(
        this.getCatalogProducts({
          page: 1,
          per_page: 4,
          sort_filter_type: PRODUCTS_ORDER_KEY.BESTSELLERS
        }).then((result) => {
          this.popularProducts = result.data;
        })
      );

      if (this.productData.brand) {
        promiseCollection.push(
          this.getCatalogProducts({
            brand_slug: this.productData.brand.slug,
            page: 1,
            sort_filter_type: PRODUCTS_ORDER_KEY.NEWEST
          }).then((result) => {
            this.seriesProducts = result.data.filter((item) => item.id !== this.productData.id);
          })
        );
      }

      await Promise.all(promiseCollection);
      globalLoader(false);
    },

    _setMetaParams() {
      let { metaTitle, title, metaDescription, description, image } = this.productData;
      this.metaParams.title = metaTitle || title;

      let fakeEL = document.createElement('div');
      fakeEL.innerHTML = metaDescription || description;
      let plainText = fakeEL.textContent || fakeEL.innerText || '';
      let metaText =
        plainText.length > 160
          ? plainText
              .slice(0, 157)
              .trim()
              .replace(/\s+\S*$/, '') + '...'
          : plainText;
      this.metaParams.description = metaText;
      this.metaParams.image = image || null;
      this.setMeta();
    },

    onChangeFavorite() {
      if (this.isAuthenticated) {
        const findItem = this.favourites.find((e) => e.productId === this.productData.id);
        if (findItem > -1) {
          this.removeFromFavorite(findItem.id).then(() => {
            this.getFavorites();
            this.isFavorite = false;
            this.$toasted.success(this.$t('toast.successRemoveFromFavorite'));
          });
        } else {
          this.addToFavorite({ product_id: this.productData.id }).then(() => {
            this.isFavorite = true;
            this.$toasted.success(this.$t('toast.successAddToFavorites'));
            this.getFavorites();
          });
        }
      } else {
        this.changeLoginPopup(true);
      }
    }
  }
};
